/**
 * OKX REST API Client
 * @see https://www.okx.com/docs-v5/en/
 */

import type {
  OkxDepthResponse,
  OkxInstrumentsResponse,
  OkxKlinesResponse,
  OkxTickerResponse,
  OkxTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://www.okx.com';

/**
 * OKX REST API endpoints.
 */
export const okxRestEndpoints = {
  instruments: `${BASE_URL}/api/v5/public/instruments`,
  ticker: `${BASE_URL}/api/v5/market/ticker`,
  klines: `${BASE_URL}/api/v5/market/candles`,
  trades: `${BASE_URL}/api/v5/market/trades`,
  depth: `${BASE_URL}/api/v5/market/books`,
} as const;

// ========================================
// Helper Functions
// ========================================

/**
 * Format symbol for OKX API calls.
 * OKX uses dash-separated format: "BTC-USDT", "ETH-USDT".
 *
 * @example
 * formatOkxSymbol("BTC", "USDT") // "BTC-USDT"
 */
export function formatOkxSymbol(base: string, quote: string): string {
  return `${base.toUpperCase()}-${quote.toUpperCase()}`;
}

/**
 * Parse OKX symbol into base/quote.
 *
 * @example
 * parseOkxSymbol("BTC-USDT") // { base: "BTC", quote: "USDT" }
 */
export function parseOkxSymbol(symbol: string): { base: string; quote: string } | null {
  const parts = symbol.split('-');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return { base: parts[0], quote: parts[1] };
}

/**
 * Validate OKX API response status.
 * Throws error if code is not "0".
 */
export function validateOkxResponse<T extends { code: string; msg: string }>(
  response: T,
  operation: string,
): void {
  if (response.code !== '0') {
    throw new Error(`OKX ${operation} failed: code=${response.code}, msg=${response.msg}`);
  }
}

// ========================================
// REST API Functions
// ========================================

/**
 * Request parameters for getOkxInstruments.
 */
export interface OkxInstrumentsRequest extends Record<string, unknown> {
  /** Instrument type: SPOT, MARGIN, SWAP, FUTURES, OPTION */
  instType: string;
  /** Instrument ID (optional, for specific instrument) */
  instId?: string;
}

/**
 * Fetches all trading instruments and their metadata from OKX.
 * @see https://www.okx.com/docs-v5/en/#public-data-rest-api-get-instruments
 */
export const getOkxInstruments = async (
  params: OkxInstrumentsRequest,
  options?: RestRequestOptions,
): Promise<OkxInstrumentsResponse> => {
  const response = await get<OkxInstrumentsResponse>({
    url: okxRestEndpoints.instruments,
    params,
    options,
  });

  validateOkxResponse(response.data, 'getOkxInstruments');

  return response.data;
};

/**
 * Request parameters for getOkxTicker.
 */
export interface OkxTickerRequest extends Record<string, unknown> {
  /** Instrument ID (e.g., BTC-USDT) */
  instId: string;
}

/**
 * Fetches 24-hour ticker statistics for a symbol.
 * @see https://www.okx.com/docs-v5/en/#order-book-trading-market-data-get-ticker
 */
export const getOkxTicker = async (
  params: OkxTickerRequest,
  options?: RestRequestOptions,
): Promise<OkxTickerResponse> => {
  const response = await get<OkxTickerResponse>({
    url: okxRestEndpoints.ticker,
    params,
    options,
  });

  validateOkxResponse(response.data, 'getOkxTicker');

  return response.data;
};

/**
 * Request parameters for getOkxKlines.
 *
 * OKX Pagination Note:
 * - `after`: Returns records with ts < after (older/earlier data)
 * - `before`: Returns records with ts > before (newer/later data)
 * - To fetch historical data ending at time T, use after=T
 * - To fetch data in a range [A, B], use before=A and after=B
 */
export interface OkxKlinesRequest {
  /** Instrument ID (e.g., BTC-USDT) */
  instId: string;
  /** Bar interval: 1m, 3m, 5m, 15m, 30m, 1H, 2H, 4H, 6H, 12H, 1D, 1W, 1M, 3M, 6M, 1Y */
  bar?: string;
  /** Pagination: Return records EARLIER than this timestamp (ts < after) */
  after?: string;
  /** Pagination: Return records NEWER than this timestamp (ts > before) */
  before?: string;
  /** Number of results per request (max 300, default 100) */
  limit?: string;
}

/**
 * Fetches historical klines (candlesticks) from OKX.
 * IMPORTANT: Timestamps in response are in MILLISECONDS.
 * IMPORTANT: Data is returned in reverse chronological order (newest first).
 *
 * @see https://www.okx.com/docs-v5/en/#order-book-trading-market-data-get-candlesticks
 */
export const getOkxKlines = async (
  params: OkxKlinesRequest,
  options?: RestRequestOptions,
): Promise<OkxKlinesResponse> => {
  const response = await get<OkxKlinesResponse>({
    url: okxRestEndpoints.klines,
    params: {
      instId: params.instId,
      bar: params.bar ?? '1m',
      ...(params.after && { after: params.after }),
      ...(params.before && { before: params.before }),
      ...(params.limit && { limit: params.limit }),
    },
    options,
  });

  validateOkxResponse(response.data, 'getOkxKlines');

  return response.data;
};

/**
 * Request parameters for getOkxTrades.
 */
export interface OkxTradesRequest {
  /** Instrument ID (e.g., BTC-USDT) */
  instId: string;
  /** Number of results per request (max 500, default 100) */
  limit?: string;
}

/**
 * Fetches recent public trades for a symbol.
 * @see https://www.okx.com/docs-v5/en/#order-book-trading-market-data-get-trades
 */
export const getOkxTrades = async (
  params: OkxTradesRequest,
  options?: RestRequestOptions,
): Promise<OkxTradesResponse> => {
  const response = await get<OkxTradesResponse>({
    url: okxRestEndpoints.trades,
    params: {
      instId: params.instId,
      limit: params.limit ?? '100',
    },
    options,
  });

  validateOkxResponse(response.data, 'getOkxTrades');

  return response.data;
};

/**
 * Request parameters for getOkxDepth.
 */
export interface OkxDepthRequest {
  /** Instrument ID (e.g., BTC-USDT) */
  instId: string;
  /** Order book depth (1-400, default 1 = top 1 level, 400 = top 400 levels) */
  sz?: string;
}

/**
 * Fetches orderbook depth (snapshot) for a symbol.
 * @see https://www.okx.com/docs-v5/en/#order-book-trading-market-data-get-order-book
 */
export const getOkxDepth = async (
  params: OkxDepthRequest,
  options?: RestRequestOptions,
): Promise<OkxDepthResponse> => {
  const response = await get<OkxDepthResponse>({
    url: okxRestEndpoints.depth,
    params: {
      instId: params.instId,
      sz: params.sz ?? '400',
    },
    options,
  });

  validateOkxResponse(response.data, 'getOkxDepth');

  return response.data;
};
